import i18n from "../../../locales/i18n";
import {
  includeBsadInput,
  mainTemplateColorInput,
  mainListingInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  firstImageOfListingTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const sold_property_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.soldProperty.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-sold-property-1.jpg",
  description: i18n.t("creativesGenerator.templates.soldProperty.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    firstImageOfListingTemplateInput,
    {
      label: i18n.t("creativesGenerator.templates.soldProperty.soldBannerText"),
      type: "text",
      nameInPrompt: "sold_banner_text",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.soldProperty.soldBannerTextDescription"),
      defaultValue: () =>
        i18n.t("creativesGenerator.templates.soldProperty.soldBannerTextDefaultValue")
    },
    {
      label: i18n.t("creativesGenerator.templates.soldProperty.propertyDescriptionLine1"),
      type: "text",
      nameInPrompt: "property_description_line_1",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.soldProperty.propertyDescriptionLine1Description"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const neighborhood = mainListing?.address?.neighborhood ?? "_____";
        return i18n.t(
          "creativesGenerator.templates.soldProperty.propertyDescriptionLine1DefaultValue",
          { neighborhood }
        );
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.soldProperty.propertyDescriptionLine2"),
      type: "text",
      nameInPrompt: "property_description_line_2",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.soldProperty.propertyDescriptionLine2Description"
      ),
      defaultValue: () =>
        i18n.t("creativesGenerator.templates.soldProperty.propertyDescriptionLine2DefaultValue")
    },
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, mainColorName, lighterColorValue, darkerColorValue } =
      getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    const getTextValue = (nameInPrompt: string) =>
      textParams.find((param) => param.nameInPrompt === nameInPrompt)?.finalValue ?? "";

    return `
{
  "description": "Sold Property - Green Foliage SOLD Announcement",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "purpose": "Celebratory 'Sold' announcement for a property with elegant green foliage SOLD text",
    "tone": "Professional, celebratory, elegant, and nature-inspired",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "vertical_split",
    "sections": {
      "top_section": {
        "height": "55%",
        "content": {
          "type": "property_image",
          "source": "listing_image",
          "style": "Full-width property photo, object_fit: cover, filling the entire top section",
          "instructions": "Use the provided listing image as-is. Do NOT alter it beyond resizing/cropping to fit the area."
        },
        "bottom_border": {
          "type": "curved_wave",
          "style": "A smooth concave curve/wave shape at the bottom edge of the image section. The curve dips downward in the center creating an elegant arch. The curve color is ${mainColorValue} (${mainColorName}), approximately 4-5px thick, creating a clean separation between the photo and the white section below.",
          "instructions": "The green curved wave acts as the visual boundary between the property image above and the white content area below. The image clips at this curved border."
        }
      },
      "sold_banner": {
        "position": "overlapping left side, between the property image and the white section",
        "style": "A black angled ribbon/tag tilted slightly (~-5 degrees). Contains bold white Hebrew text. The banner has a slight 3D shadow effect. Positioned so it overlaps the boundary between the image and white area.",
        "text": "${getTextValue("sold_banner_text")}",
        "text_style": "Bold white Hebrew text, large font with exclamation mark, font_size: extra-large"
      },
      "middle_section": {
        "height": "33%",
        "background": "white",
        "content": [
          {
            "type": "hero_text",
            "text": "SOLD",
            "position": "center of the white section",
            "style": "Massive 'SOLD' letters where each letter is filled with a lush green foliage/leaves texture. The letters should look like they are made entirely of green plant leaves and small bushes. The foliage should be realistic, dense, and vibrant green. The letters have slight 3D depth/shadow effect. This is the dominant visual element of the middle section.",
            "font_size": "very large, taking up significant width of the design"
          },
          {
            "type": "description_text",
            "lines": [
              {
                "text": "${getTextValue("property_description_line_1")}",
                "style": "Bold dark gray/black Hebrew text, font_size: large, text-align: center, RTL"
              },
              {
                "text": "${getTextValue("property_description_line_2")}",
                "style": "Bold dark gray/black Hebrew text, font_size: large, text-align: center, RTL"
              }
            ],
            "position": "below the SOLD text, centered",
            "separator": {
              "type": "horizontal_lines",
              "style": "Thin ${mainColorValue} (${mainColorName}) horizontal lines above and below the description text block, spanning about 60% of the width, centered"
            }
          }
        ]
      },
      "footer_section": {
        "height": "12%",
        "background": "white",
        "content": {
          "contact_info": {
            "position": "center-bottom",
            "elements": [
              {
                "type": "phone_number",
                "text": "${getTextValue("realtor_phone_number")}",
                "style": "Dark text, font_size: medium, centered"
              },
              {
                "type": "separator",
                "style": "vertical bar '|' between phone and name"
              },
              {
                "type": "realtor_name",
                "text": "${getTextValue("realtor_name")}",
                "style": "Bold dark Hebrew text, font_size: medium"
              }
            ],
            "separator_lines": {
              "style": "Thin ${mainColorValue} horizontal lines above the contact info, spanning full width"
            }
          }
        }
      }
    }
  },
  ${
    hasRealtorImage(params)
      ? `${getRealtorImagePrompt(
          params,
          "The realtor should be placed on the side of the footer area, overlapping slightly into the middle section above. Medium size, professional cutout with no background. The realtor appears in front of the white background."
        )},`
      : ""
  }
  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "This is an RTL (right-to-left) Hebrew design - all Hebrew text must be right-aligned or centered as specified",
    "The SOLD text MUST be made of realistic green foliage/leaves texture - this is the signature visual element of this template",
    "The curved green wave border between the property image and white section is essential to the design",
    "The 'sold' banner (black angled ribbon) should be on the LEFT side, overlapping the boundary between image and white area",
    "The white section should be clean and uncluttered with the SOLD foliage text as the focal point",
    "Green horizontal line separators should frame the property description text",
    "DO NOT alter the provided property image beyond resizing/cropping as necessary for composition",
    ${
      hasRealtorImage(params)
        ? `"The realtor cutout should be on the right side of the footer area",`
        : ""
    }
    "The overall color accent is ${mainColorValue} (${mainColorName}) - used in the wave border, line separators, and foliage color tone"
  ]
}`;
  }
};
